import { useRef } from 'react'
import {
  useRemoteVideoTileState,
  useLocalVideo,
  useToggleLocalMute,
  RemoteVideo,
  LocalVideo
} from 'amazon-chime-sdk-component-library-react'
import { useMeetingStore } from '@/stores/meeting'
import { SpeakingIndicator } from '@components/atoms/SpeakingIndicator/SpeakingIndicator'
import { ParticipantBadge } from '@components/atoms/ParticipantBadge/ParticipantBadge'
import { useLocalUserSpeaking } from '@hooks/useLocalUserSpeaking'
import { useLocalParticipantInitials } from '@hooks/useLocalParticipantInitials'
import { useDeviceOrientation } from '@hooks/useDeviceOrientation'
import { useRemoteParticipantMuteState } from '@hooks/useRemoteParticipantMuteState'
import useRemoteParticipants from '@hooks/useRemoteParticipants'
import useRemoteParticipantSpeaking from '@hooks/useRemoteParticipantSpeaking'
import { useRemoteParticipantDisplay } from './hooks/useRemoteParticipantDisplay'
import { DraggableLocalVideo } from './components/DraggableLocalVideo'

export const MeetingVideoGridMobile = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { tiles: allRemoteVideoTiles } = useRemoteVideoTileState()
  const { isVideoEnabled } = useLocalVideo()
  const localAttendeeId = useMeetingStore((state) => state.attendeeId)

  const isLocalSpeaking = useLocalUserSpeaking(0.3)
  const { initials, firstName } = useLocalParticipantInitials()
  const { muted } = useToggleLocalMute()
  const { hasRemoteParticipants } = useRemoteParticipants()
  const isRemoteSpeaking = useRemoteParticipantSpeaking()
  const isRemoteMuted = useRemoteParticipantMuteState()
  const orientation = useDeviceOrientation()

  // Guard: Only use video tiles when we know who the local user is
  const remoteVideoTiles = localAttendeeId ? allRemoteVideoTiles : []
  const hasRemoteVideo = remoteVideoTiles.length > 0

  const { name: participantName, initials: participantInitials } = useRemoteParticipantDisplay()

  // Aspect ratio based on device orientation
  const aspectRatioClass = orientation === 'portrait' ? 'aspect-[9/16]' : 'aspect-video'

  return (
    <div className="flex md:hidden w-full h-full">
      {!hasRemoteParticipants ? (
        // Alone: Show only local video centered with proper aspect ratio
        <div className="flex w-full h-full">
          <div className={`relative ${aspectRatioClass} w-full max-h-full overflow-hidden bg-surface-dark shadow-lg`}>
            <div className="absolute inset-0 flex items-center justify-center">
              {isVideoEnabled ? (
                <LocalVideo className="!absolute !inset-0 !h-full !w-full !object-cover [&>video]:!absolute [&>video]:!inset-0 [&>video]:!h-full [&>video]:!w-full [&>video]:!object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-surface-dark">
                  <SpeakingIndicator isSpeaking={isLocalSpeaking && !isVideoEnabled} size="md">
                    <div className="flex h-24 w-24 items-center justify-center rounded-full bg-surface-medium">
                      <span className="text-4xl font-bold text-primary">{initials}</span>
                    </div>
                  </SpeakingIndicator>
                </div>
              )}
            </div>

            <div className="pointer-events-none">
              <ParticipantBadge name={firstName} position="bottom-left" isMuted={muted} />
            </div>
          </div>
        </div>
      ) : (
        // With participant: Remote video takes full screen, local video draggable on top
        <div ref={containerRef} className="relative w-full h-full bg-surface-dark">
          {/* Remote Video - Full screen with margins on all sides */}
          <div className="relative w-full h-full overflow-hidden bg-surface-dark shadow-lg">
            <div className="absolute inset-0 flex items-center justify-center">
              {hasRemoteVideo ? (
                <RemoteVideo
                  tileId={remoteVideoTiles[0]}
                  className="!absolute !inset-0 !h-full !w-full !object-cover [&>video]:!absolute [&>video]:!inset-0 [&>video]:!h-full [&>video]:!w-full [&>video]:!object-cover"
                />
              ) : (
                <SpeakingIndicator isSpeaking={isRemoteSpeaking && !hasRemoteVideo} size="lg">
                  <div className="flex h-32 w-32 items-center justify-center rounded-full bg-surface-medium">
                    <span className="text-5xl font-bold">{participantInitials}</span>
                  </div>
                </SpeakingIndicator>
              )}
            </div>

            {/* Remote participant badge */}
            <div className="pointer-events-none">
              <ParticipantBadge name={participantName} position="bottom-left" isMuted={isRemoteMuted} />
            </div>
          </div>

          {/* Draggable Local Video - Overlay on top, can be moved anywhere */}
          <DraggableLocalVideo containerRef={containerRef} platform="mobile" />
        </div>
      )}
    </div>
  )
}
